import { WebContainer, type WebContainerProcess } from '@webcontainer/api';
import { workbenchStore } from '../stores/workbench';
import { appendTerminalLine, appendTerminalProcessOutput } from '../stores/terminal';
import { getHeadlessTemplateFiles } from '../templates/headlessTemplate';
import { materializeHotEditBridgeFilesInStore } from '../utils/mergedFiles';
import { wcPerfMark, wcPerfMeasure } from '../utils/workbenchPerf';
import { recordWorkbenchMemory } from '../utils/workbenchMemoryTelemetry';
import { npmFrozenInstallCliArgs } from './npmFrozenInstall';
import { persistAssetFile } from '../runtime/tool-adapters/writeAsset';
import type {
  WorkbenchPreviewRecoveryReason,
  WorkbenchPreviewRecoveryStep,
} from '../stores/workbench';
import {
  DEV_SERVER_RESTART_BACKOFF_MS,
  PREVIEW_RECOVERY_DEADLINE_MS,
  PREVIEW_RECOVERY_STEPS,
  nextPreviewRecoveryStart,
  recoveryStartForWorkspace,
  runPreviewRecovery,
  type PreviewRecoveryRun,
} from './previewRecovery';
import { WORKBENCH_SPAWN_DEADLINE_MS, isWebContainerResponsive, withDeadline } from './deadline';
import { WorkbenchUnsupportedDeviceError, detectWorkbenchCapability } from './capability';
import {
  WORKBENCH_TAB_CHANNEL,
  WorkbenchTabInUseError,
  createTabOwnership,
  type ChannelLike,
  type LockManagerLike,
  type TabOwnership,
  workbenchTabSlots,
} from './tabOwnership';
import {
  fetchPrebuiltNodeModules,
  getPrebuiltNodeModulesUrl,
  getTemplateLockHash,
  mountDepsFromBuffer,
  readDepsCacheFromOpfs,
  removeDepsCacheFromOpfs,
  scheduleIdleNodeModulesExport,
} from './depsCache';

const PERSIST_KEY = '__ideozWorkbenchWebContainer' as const;

type PersistedWc = {
  instance: WebContainer | null;
  bootPromise: Promise<WebContainer> | null;
  templateReady: Promise<void> | null;
  lastPreviewUrl?: string;
  templateDepsInstalled?: boolean;
  productionBuildSucceeded?: boolean;
  devServerProcess?: WebContainerProcess | null;
  devServerRestartAttempts?: number;
  serverReadyTimeoutId?: ReturnType<typeof setTimeout> | null;
  /** The running `recoverPreviewEnvironment` call; automatic restarts stand down while set. */
  recoveryPromise?: Promise<boolean> | null;
  /** Recent recovery runs in this tab, used to escalate instead of repeating a step. */
  recoveryHistory?: PreviewRecoveryRun[];
  tabOwnership?: TabOwnership;
  pagehideTeardownInstalled?: boolean;
  /** Lock hash of the `node_modules` mounted or installed in this instance. */
  mountedDepsLockHash?: string;
  /** Which Vite command `devServerProcess` runs. */
  devServerKind?: ViteServerKind;
};

function getPersisted(): PersistedWc {
  const g = globalThis as typeof globalThis & { [PERSIST_KEY]?: PersistedWc };
  if (!g[PERSIST_KEY]) {
    g[PERSIST_KEY] = {
      instance: null,
      bootPromise: null,
      templateReady: null,
      devServerProcess: null,
      devServerRestartAttempts: 0,
      serverReadyTimeoutId: null,
    };
  }
  return g[PERSIST_KEY];
}

function restoreWorkbenchSessionAfterRemount(): void {
  const s = getPersisted();
  if (!s.instance) return;
  if (s.lastPreviewUrl && !workbenchStore.previewUrl.get()) {
    workbenchStore.previewUrl.set(s.lastPreviewUrl);
  }
  if (s.templateDepsInstalled) {
    workbenchStore.depsInstalled.set(true);
  }
  if (s.productionBuildSucceeded) {
    workbenchStore.buildStatus.set('success');
  }
}

export function syncWorkbenchStoreWithWebContainerPersist(): void {
  restoreWorkbenchSessionAfterRemount();
}

export function markTemplateDependenciesInstalled(): void {
  getPersisted().templateDepsInstalled = true;
}

export function recordProductionBuildOutcome(ok: boolean): void {
  getPersisted().productionBuildSucceeded = ok;
}

/**
 * Clear the cross-remount build/preview cache (`lastPreviewUrl`, `productionBuildSucceeded`,
 * `templateDepsInstalled`) without touching the WebContainer instance itself — the instance
 * and dev server are a single `globalThis` singleton shared across every conversation in the
 * tab, but this cache is meant to survive only a same-conversation remount (e.g. reopening the
 * prototype panel). `workbenchStore.reset()` clears the *reactive* store on every remount, but
 * never touched this separate cache, so `restoreWorkbenchSessionAfterRemount` would immediately
 * re-apply a previous, unrelated conversation's "build succeeded" / preview URL right back on
 * top of the reset — call this once, on a genuine conversation switch (not on every remount),
 * so the next remount's restore has nothing stale left to re-apply.
 */
export function resetPersistedBuildCache(): void {
  const s = getPersisted();
  s.lastPreviewUrl = undefined;
  // Keep templateDepsInstalled — node_modules belongs to the shared WebContainer
  // singleton, not to a conversation. Clearing it here left depsInstalled false
  // after workbenchStore.reset() on remount with no path to set it again.
  s.productionBuildSucceeded = false;
}

export type SnapshotPreviewFinalizeOptions = {
  buildVerified?: boolean;
  knownBuildSuccess?: boolean;
};

export function markSnapshotPreviewReady(options?: SnapshotPreviewFinalizeOptions): void {
  workbenchStore.snapshotPreviewReady.set(true);
  if (options?.buildVerified === true || options?.knownBuildSuccess === true) {
    workbenchStore.buildStatus.set('success');
    recordProductionBuildOutcome(true);
  }
  syncWorkbenchStoreWithWebContainerPersist();
}

async function killDevServer(): Promise<void> {
  const s = getPersisted();
  if (s.serverReadyTimeoutId != null) {
    clearTimeout(s.serverReadyTimeoutId);
    s.serverReadyTimeoutId = null;
  }
  const proc = s.devServerProcess;
  if (!proc) return;
  s.devServerProcess = null;
  try {
    await proc.kill();
  } catch {
    /* already dead */
  }
}

export function restartDevServerAfterSnapshot(wc: WebContainer): Promise<void> {
  return startDevServer(wc, { forceRestart: true });
}

/**
 * Stops Vite before a snapshot restore writes dozens of files, so its watcher never transforms
 * a half-written import graph and shows an import-analysis overlay (SN-5). The caller starts it
 * again once, with `restartDevServerAfterSnapshot`, after every file is in place.
 */
export async function stopDevServerForRestore(): Promise<void> {
  await killDevServer();
  workbenchStore.devServerStatus.set('starting');
}

const PREVIEW_PORT = 3111;

type ViteServerKind = 'dev' | 'preview';

/**
 * `WebContainer.boot()` occasionally never settles — the SDK's own worker handshake
 * can wedge (visible in devtools as its fetch worker being preloaded but never used)
 * with no rejection, no timeout of its own, and no way to cancel it. Without this
 * guard the boot promise hangs forever: `bootStatus` never leaves 'booting', nothing
 * is ever shown, and — since the wedge lives in the browser's WebContainer/worker
 * layer rather than any state this app owns — even a plain page refresh doesn't
 * clear it. Racing it against a deadline turns that silent hang into a bounded,
 * user-visible error the existing boot `.catch` (in usePrototyperWorkbenchFacade)
 * can surface and let the user retry.
 */
const WEBCONTAINER_BOOT_TIMEOUT_MS = 45_000;

/** How long this tab has been hidden, 0 while visible (P6.4e: a long-hidden workbench is idle). */
function createHiddenClock(): () => number {
  if (typeof document === 'undefined') return () => 0;
  let hiddenAt: number | null = document.visibilityState === 'hidden' ? Date.now() : null;
  document.addEventListener('visibilitychange', () => {
    hiddenAt = document.visibilityState === 'hidden' ? (hiddenAt ?? Date.now()) : null;
  });
  return () => (hiddenAt == null ? 0 : Date.now() - hiddenAt);
}

function getTabOwnership(): TabOwnership {
  const s = getPersisted();
  if (s.tabOwnership) return s.tabOwnership;

  const flag = import.meta.env?.VITE_WORKBENCH_TAB_OWNERSHIP;
  const enabled = !(flag === '0' || flag === 'false');
  const locks =
    enabled && typeof navigator !== 'undefined' && navigator.locks
      ? (navigator.locks as unknown as LockManagerLike)
      : null;
  const channel =
    enabled && typeof BroadcastChannel !== 'undefined'
      ? (new BroadcastChannel(WORKBENCH_TAB_CHANNEL) as unknown as ChannelLike)
      : null;

  s.tabOwnership = createTabOwnership({
    tabId: globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`,
    locks,
    channel,
    // Several tabs may each run a workbench, capped per device (P6.4e).
    slots: workbenchTabSlots(
      detectWorkbenchCapability().tier,
      import.meta.env?.VITE_WORKBENCH_MAX_TABS,
    ),
    hiddenForMs: createHiddenClock(),
    // The conversation facade links the container into the store only when a workbench is shown.
    isInUse: () => workbenchStore.webcontainer.get() != null,
    isBusy: () =>
      workbenchStore.isStreaming.get() ||
      workbenchStore.clientToolsBusy.get() ||
      workbenchStore.buildStatus.get() === 'building',
    onReleased: () => {
      teardownWebContainer();
      workbenchStore.webcontainer.set(null);
      workbenchStore.previewUrl.set('');
      workbenchStore.devServerStatus.set('idle');
    },
    onStateChange: (state) => workbenchStore.tabOwnership.set(state),
  });
  return s.tabOwnership;
}

/** Frees the WebContainer's memory as soon as the tab goes away instead of at GC. */
function installPagehideTeardown(): void {
  const s = getPersisted();
  if (s.pagehideTeardownInstalled || typeof window === 'undefined') return;
  s.pagehideTeardownInstalled = true;
  window.addEventListener('pagehide', (event) => {
    // A page kept in the back/forward cache comes back with its container; keep it.
    if (!event.persisted) teardownWebContainer();
  });
}

/**
 * "Use it here" when another tab owns the workbench. `owner`: this tab now holds the lock —
 * reload so the conversation boots and restores through its normal path.
 */
export function claimWorkbenchForThisTab(): Promise<'owner' | 'busy' | 'no-answer'> {
  return getTabOwnership().claim();
}

/**
 * `takeOverIdleTab: false` (prewarm): never ask another tab to give up its WebContainer just
 * to warm up a tab the founder may not use.
 */
export async function bootWebContainer(opts?: { takeOverIdleTab?: boolean }): Promise<WebContainer> {
  const s = getPersisted();
  if (s.instance) {
    restoreWorkbenchSessionAfterRemount();
    return s.instance;
  }

  if (!globalThis.crossOriginIsolated) {
    throw new Error(
      '[Workbench] crossOriginIsolated is false — WebContainer requires COOP/COEP headers. ' +
        'Ensure Cross-Origin-Opener-Policy: same-origin and Cross-Origin-Embedder-Policy: credentialless are set.',
    );
  }

  const capability = detectWorkbenchCapability();
  if (capability.tier === 'view-only') {
    throw new WorkbenchUnsupportedDeviceError(capability);
  }
  installPagehideTeardown();

  if (!s.bootPromise) {
    const p: Promise<WebContainer> = getTabOwnership()
      .ensure({ takeOverIdleTab: opts?.takeOverIdleTab ?? true })
      .then((owns) => {
        if (!owns) throw new WorkbenchTabInUseError();
        return bootWithTimeout(s);
      });
    // Clears a failed or timed-out boot so the next call can try again instead of
    // re-awaiting a rejected promise forever.
    p.catch(() => {
      if (s.bootPromise === p) s.bootPromise = null;
    });
    s.bootPromise = p;
  }

  return s.bootPromise.then((wc) => {
    restoreWorkbenchSessionAfterRemount();
    return wc;
  });
}

function bootWithTimeout(s: PersistedWc): Promise<WebContainer> {
  wcPerfMark('wc:boot:start');
  const bootAttempt = WebContainer.boot()
    .then((wc) => {
      wcPerfMark('wc:boot:end');
      wcPerfMeasure('wc:boot:start', 'wc:boot:end', 'wc:WebContainer.boot');
      s.instance = wc;
      recordWorkbenchMemory('boot');
      wc.on('server-ready', (_port, url) => {
        if (s.serverReadyTimeoutId != null) {
          clearTimeout(s.serverReadyTimeoutId);
          s.serverReadyTimeoutId = null;
        }
        // A crash-episode's 1-retry budget should reset once the server proves it's
        // healthy again — otherwise two unrelated crashes anywhere in the tab's life
        // permanently disable auto-restart for every crash after that.
        s.devServerRestartAttempts = 0;
        s.lastPreviewUrl = url;
        workbenchStore.previewUrl.set(url);
        workbenchStore.devServerStatus.set('ready');
        wcPerfMark('wc:timeToFirstPreview');
        restoreWorkbenchSessionAfterRemount();
      });
      s.templateReady = mountHeadlessTemplate(wc).catch((err: unknown) => {
        s.templateReady = null;
        // A cold first-time generation never calls `waitForTemplateReady()` — nothing else
        // is awaiting this promise, so without this it's an unhandled rejection and the UI
        // sits on "Setting up your prototype workspace…" (`!depsInstalled`) forever with no
        // error. Publish to the store directly so `WorkbenchPanel`'s existing boot-error UI
        // (Retry/Refresh) picks it up regardless of who — if anyone — is awaiting this.
        workbenchStore.bootStatus.set('error');
        workbenchStore.bootError.set(
          err instanceof Error ? err.message : 'Failed to set up the prototype workspace',
        );
        throw err;
      });
      return wc;
    });

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(
        new Error(
          `WebContainer did not finish booting within ${Math.round(WEBCONTAINER_BOOT_TIMEOUT_MS / 1000)}s. ` +
            'This can happen when a previous WebContainer session in this tab did not shut down cleanly — ' +
            'try closing other tabs running the workbench, or refresh the page.',
        ),
      );
    }, WEBCONTAINER_BOOT_TIMEOUT_MS);
  });

  // `bootWebContainer` clears `s.bootPromise` when this rejects, whether the boot itself
  // failed or the timeout won the race.
  return Promise.race([bootAttempt, timeout]).finally(() => {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  });
}

/**
 * Ceiling for the whole template mount, deliberately well above `TEMPLATE_INSTALL_TIMEOUT_MS`
 * (180s) so a legitimately slow cold `npm ci` is never cut short. This is not a UX timeout —
 * the UI escapes far sooner — it exists purely so this promise *always* settles.
 *
 * Why it's needed: `mountHeadlessTemplate` has exactly one bounded await (`proc.exit`). Every
 * other step — the OPFS deps-cache read, `mountDepsFromBuffer`'s `chmod`, the prebuilt-deps
 * `fetch`, `install.output.pipeTo`, and `wc.spawn('npx', ['vite', …])` — can stall forever with
 * no rejection. A template *failure* is recoverable (the `.catch` on `templateReady` sets
 * `bootStatus: 'error'`, which surfaces retry UI); a template *hang* produces no state
 * transition anywhere, so every `waitForTemplateReady()` caller blocks for the life of the page.
 * Racing a deadline converts that silent hang into a normal, recoverable error.
 */
const TEMPLATE_READY_TIMEOUT_MS = 240_000;

export async function waitForTemplateReady(): Promise<void> {
  const s = getPersisted();
  await s.bootPromise;
  if (!s.templateReady) return;

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(
        new Error(
          `Preparing the prototype workspace did not finish within ${Math.round(
            TEMPLATE_READY_TIMEOUT_MS / 1000,
          )}s. Retry, or reload the page.`,
        ),
      );
    }, TEMPLATE_READY_TIMEOUT_MS);
  });

  try {
    await Promise.race([s.templateReady, timeout]);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}

export async function retryTemplateMount(): Promise<void> {
  const s = getPersisted();
  if (!s.instance) {
    await bootWebContainer();
    return;
  }
  s.templateReady = mountHeadlessTemplate(s.instance).catch((err: unknown) => {
    s.templateReady = null;
    throw err;
  });
  await s.templateReady;
}

export function getWebContainer(): WebContainer | null {
  return getPersisted().instance;
}

export function teardownWebContainer(): void {
  const s = getPersisted();
  void killDevServer();
  if (s.instance) {
    s.instance.teardown();
    s.instance = null;
    s.bootPromise = null;
    s.templateReady = null;
    s.lastPreviewUrl = undefined;
    s.templateDepsInstalled = undefined;
    s.productionBuildSucceeded = undefined;
    s.mountedDepsLockHash = undefined;
    s.devServerKind = undefined;
  }
}

const TEMPLATE_FS_CONCURRENCY = 14;

/**
 * The initial template's `npm ci`/`npm install` is a real spawned process with no timeout of
 * its own — if WebContainer's virtual registry/network layer wedges (the same underlying
 * instability that can strand `WebContainer.boot()` itself, see `WEBCONTAINER_BOOT_TIMEOUT_MS`
 * above), `install.exit` never settles, `depsInstalled` never flips true, and the UI is stuck on
 * "Setting up your prototype workspace…" forever with no error and no recovery short of clearing
 * site data. Mirrors `waitForExitWithTimeout` in `runtime/tool-adapters/build.ts` (not reused
 * directly to avoid a cross-module dependency for a 10-line helper).
 */
const TEMPLATE_INSTALL_TIMEOUT_MS = 180_000;

async function waitForExitWithTimeout(
  proc: WebContainerProcess,
  timeoutMs: number,
  label: string,
): Promise<number> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      void proc.kill();
      reject(new Error(`${label} did not finish within ${Math.round(timeoutMs / 1000)}s and was killed.`));
    }, timeoutMs);
  });
  try {
    return await Promise.race([proc.exit, timeout]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

async function runPool<T>(items: T[], limit: number, fn: (item: T) => Promise<void>): Promise<void> {
  if (items.length === 0) return;
  let i = 0;
  const worker = async (): Promise<void> => {
    for (;;) {
      const idx = i++;
      if (idx >= items.length) break;
      await fn(items[idx]);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, () => worker()),
  );
}

/**
 * Reads one archive and mounts it within this call, so nothing keeps the (tens of MB) buffer
 * reachable once `wc.mount` has copied it into the WebContainer (WC-1).
 */
async function mountDepsArchive(
  wc: WebContainer,
  source: 'opfs' | 'prebuilt',
  lockHash: string,
): Promise<'mounted' | 'missing' | 'invalid'> {
  const buffer =
    source === 'opfs'
      ? await readDepsCacheFromOpfs(lockHash)
      : await fetchPrebuiltNodeModules(lockHash, getPrebuiltNodeModulesUrl(lockHash));
  if (!buffer) return 'missing';
  return (await mountDepsFromBuffer(wc, buffer)) ? 'mounted' : 'invalid';
}

function markDepsMounted(lockHash: string): void {
  workbenchStore.depsInstalled.set(true);
  markTemplateDependenciesInstalled();
  getPersisted().mountedDepsLockHash = lockHash;
  recordWorkbenchMemory('deps-mounted');
}

/** A stream, build, client tool or restore is running; heavy background work waits. */
function isWorkbenchBusy(): boolean {
  return (
    workbenchStore.isStreaming.get() ||
    workbenchStore.clientToolsBusy.get() ||
    workbenchStore.buildStatus.get() === 'building' ||
    workbenchStore.restoreInProgress.get()
  );
}

async function installTemplateDependencies(wc: WebContainer, files: Record<string, string>): Promise<void> {
  const lockContent = files['package-lock.json'];
  const lockHash = lockContent ? getTemplateLockHash(lockContent) : 'no-lock';

  if (lockContent) {
    const fromOpfs = await mountDepsArchive(wc, 'opfs', lockHash);
    if (fromOpfs === 'mounted') {
      appendTerminalLine('Restored node_modules from OPFS cache.', 'info');
      markDepsMounted(lockHash);
      return;
    }
    if (fromOpfs === 'invalid') {
      void removeDepsCacheFromOpfs(lockHash);
    }

    // `fetchPrebuiltNodeModules` already keeps this archive in the Cache API, so it is not
    // exported into OPFS as well: that export built a second full archive in memory.
    if ((await mountDepsArchive(wc, 'prebuilt', lockHash)) === 'mounted') {
      appendTerminalLine('Restored node_modules from prebuilt snapshot.', 'info');
      markDepsMounted(lockHash);
      return;
    }
  }

  const hasPkgLock = Boolean(files['package-lock.json']);
  const npmArgs = npmFrozenInstallCliArgs(hasPkgLock);
  appendTerminalLine(`Template mounted. Running npm ${npmArgs[0]}…`, 'info');

  const npmStart = hasPkgLock ? 'wb:npm:ci:start' : 'wb:npm:install:start';
  const npmEnd = 'wb:npm:template:end';
  wcPerfMark(npmStart);
  const install = await wc.spawn('npm', npmArgs);
  const chunks: string[] = [];
  const writer = new WritableStream<string>({
    write(chunk) {
      chunks.push(chunk);
    },
  });
  const installPiping = install.output.pipeTo(writer);
  const exitCode = await waitForExitWithTimeout(
    install,
    TEMPLATE_INSTALL_TIMEOUT_MS,
    `npm ${npmArgs[0]}`,
  );
  await installPiping;
  appendTerminalProcessOutput(chunks.join(''), exitCode !== 0 ? 'stderr' : 'stdout');
  wcPerfMark(npmEnd);
  wcPerfMeasure(npmStart, npmEnd, hasPkgLock ? 'wc:template:npmCi' : 'wc:template:npmInstall');

  if (exitCode !== 0) {
    appendTerminalLine(`npm ${npmArgs[0]} failed (exit ${exitCode})`, 'stderr');
    throw new Error(`npm ${npmArgs[0]} failed:\n${chunks.join('')}`);
  }

  markDepsMounted(lockHash);
  appendTerminalLine('Dependencies installed.', 'info');

  if (lockContent) {
    // Not now: the first build and preview are about to run, and the export holds the whole
    // tree in memory. `scheduleIdleNodeModulesExport` waits until nothing heavy is running.
    scheduleIdleNodeModulesExport(wc, lockHash, { isBusy: isWorkbenchBusy });
  }
}

async function mountHeadlessTemplate(wc: WebContainer): Promise<void> {
  const files = getHeadlessTemplateFiles();
  const paths = Object.keys(files);

  appendTerminalLine(`Mounting headless template (${paths.length} files)…`, 'info');

  wcPerfMark('wc:templateFs:start');
  const dirs = new Set<string>();
  for (const filePath of paths) {
    const dir = filePath.includes('/') ? filePath.substring(0, filePath.lastIndexOf('/')) : '';
    if (dir) dirs.add(dir);
  }
  const sortedDirs = [...dirs].sort((a, b) => a.split('/').length - b.split('/').length);
  for (const d of sortedDirs) {
    await wc.fs.mkdir(d, { recursive: true });
  }
  await runPool(paths, TEMPLATE_FS_CONCURRENCY, (filePath) => wc.fs.writeFile(filePath, files[filePath]));
  wcPerfMark('wc:templateFs:end');
  wcPerfMeasure('wc:templateFs:start', 'wc:templateFs:end', 'wc:mount:templateFsWrites');

  await installTemplateDependencies(wc, files);

  materializeHotEditBridgeFilesInStore();

  await startDevServer(wc);
}

async function spawnViteServer(
  wc: WebContainer,
  kind: ViteServerKind,
  /**
   * `keepRestartBudget`: an automatic restart after a crash. Without it the restart reset the
   * attempt counter to 0, so a server that crashed on every start restarted forever.
   */
  opts?: { forceRestart?: boolean; keepRestartBudget?: boolean },
): Promise<void> {
  const s = getPersisted();
  if (opts?.forceRestart) {
    if (!opts.keepRestartBudget) s.devServerRestartAttempts = 0;
    await killDevServer();
  }

  workbenchStore.devServerStatus.set('starting');
  const npxArgs =
    kind === 'dev'
      ? ['vite', '--port', String(PREVIEW_PORT), '--host']
      : ['vite', 'preview', '--port', String(PREVIEW_PORT), '--host'];
  appendTerminalLine(`$ npx ${npxArgs.join(' ')}`, 'info');

  if (s.serverReadyTimeoutId != null) {
    clearTimeout(s.serverReadyTimeoutId);
  }
  s.serverReadyTimeoutId = setTimeout(() => {
    if (workbenchStore.devServerStatus.get() === 'starting') {
      workbenchStore.devServerStatus.set('error');
      const label = kind === 'dev' ? 'Dev server' : 'Preview server';
      appendTerminalLine(`${label} did not become ready within 60s.`, 'stderr');
      if (kind === 'dev' && !s.recoveryPromise) {
        void recoverPreviewEnvironment('dev-server-timeout');
      }
    }
  }, 60_000);

  try {
    // A frozen WebContainer never settles `spawn`; the catch below reports `error`, which ends
    // any wait for `server-ready`.
    const proc = await withDeadline(
      wc.spawn('npx', npxArgs),
      WORKBENCH_SPAWN_DEADLINE_MS,
      'Starting the dev server',
    );
    s.devServerProcess = proc;
    s.devServerKind = kind;

    void proc.output.pipeTo(
      new WritableStream<string>({
        write(chunk) {
          appendTerminalLine(chunk);
        },
      }),
    );

    void proc.exit.then(async (code) => {
      if (s.devServerProcess !== proc) return;
      s.devServerProcess = null;
      if (code === 0) return;

      workbenchStore.devServerStatus.set('crashed');
      const label = kind === 'dev' ? 'Dev server' : 'Preview server';
      appendTerminalLine(`${label} exited with code ${code}`, 'stderr');
      // During recovery the ladder watches `devServerStatus` itself; restarting here would race it.
      if (kind !== 'dev' || s.recoveryPromise) return;

      const attempts = (s.devServerRestartAttempts ?? 0) + 1;
      s.devServerRestartAttempts = attempts;
      const delay = DEV_SERVER_RESTART_BACKOFF_MS[attempts - 1];
      if (delay === undefined) {
        // Restarts are used up (the budget resets on `server-ready`); try the heavier steps.
        void recoverPreviewEnvironment('dev-server-crashed', { startAt: 'remount-dependencies' });
        return;
      }
      appendTerminalLine(
        `Restarting dev server in ${delay / 1000}s (attempt ${attempts} of ${DEV_SERVER_RESTART_BACKOFF_MS.length})…`,
        'info',
      );
      await sleep(delay);
      if (s.devServerProcess || s.recoveryPromise || s.instance !== wc) return;
      await spawnViteServer(wc, 'dev', { forceRestart: true, keepRestartBudget: true });
    });
  } catch (err: unknown) {
    workbenchStore.devServerStatus.set('error');
    const label = kind === 'dev' ? 'dev server' : 'preview server';
    appendTerminalLine(
      `Failed to start ${label}: ${err instanceof Error ? err.message : String(err)}`,
      'stderr',
    );
  }
}

async function startDevServer(
  wc: WebContainer,
  opts?: { forceRestart?: boolean },
): Promise<void> {
  await spawnViteServer(wc, 'dev', opts);
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** How long one recovery step waits for Vite's `server-ready` before counting as failed. */
const RECOVERY_SERVER_READY_TIMEOUT_MS = 45_000;
/** `teardown()` returns before the old instance is gone; booting at once can hit "Only a single WebContainer". */
const REBOOT_SETTLE_MS = 1_000;
/** Writing every workspace file back into the container during recovery. */
const RECOVERY_WORKSPACE_WRITE_DEADLINE_MS = 60_000;
/** Reading the dependency cache and mounting it again. */
const RECOVERY_REMOUNT_DEADLINE_MS = 90_000;
/** A rebooted WebContainer mounting its template; dependencies come from the cache. */
const RECOVERY_TEMPLATE_DEADLINE_MS = 120_000;

/** Resolves true on `ready`, false on `crashed` / `error` or after `timeoutMs`. */
function waitForDevServerReady(timeoutMs: number): Promise<boolean> {
  const current = workbenchStore.devServerStatus.get();
  if (current === 'ready') return Promise.resolve(true);
  if (current === 'crashed' || current === 'error') return Promise.resolve(false);

  return new Promise((resolve) => {
    let unsubscribe = (): void => {};
    const timer = setTimeout(() => {
      unsubscribe();
      resolve(false);
    }, timeoutMs);
    unsubscribe = workbenchStore.devServerStatus.listen((status) => {
      if (status !== 'ready' && status !== 'crashed' && status !== 'error') return;
      clearTimeout(timer);
      unsubscribe();
      resolve(status === 'ready');
    });
  });
}

export function getPreviewPort(): number {
  return PREVIEW_PORT;
}

/** Lock hash of the `node_modules` in the current WebContainer, if this tab mounted or installed it. */
/**
 * After a full restore (P6.4d-2): when the restored `package-lock.json` differs from the lockfile
 * the mounted `node_modules` came from, replace them before Vite starts again — from the OPFS
 * cache, the prebuilt archive, or `npm ci`, like boot. Does nothing while the mounted lockfile is
 * unknown; the next build's own dependency check covers that case.
 */
export async function ensureDependenciesMatchLockfile(wc: WebContainer): Promise<void> {
  const mounted = getMountedDepsLockHash();
  if (!mounted) return;
  let lockContent: string;
  try {
    lockContent = await wc.fs.readFile('package-lock.json', 'utf-8');
  } catch {
    return;
  }
  if (getTemplateLockHash(lockContent) === mounted) return;
  appendTerminalLine('This version uses other dependencies; installing them…', 'info');
  workbenchStore.depsInstalled.set(false);
  await wc.fs.rm('node_modules', { recursive: true, force: true }).catch(() => undefined);
  await installTemplateDependencies(wc, { 'package-lock.json': lockContent });
}

export function getMountedDepsLockHash(): string | undefined {
  return getPersisted().mountedDepsLockHash;
}

/**
 * Makes sure the Vite **dev** server is running (the per-turn compile check and the preview
 * both use it). Resolves true once it is ready, false when it failed to come up.
 */
export async function ensureDevServerRunning(wc: WebContainer): Promise<boolean> {
  const s = getPersisted();
  if (
    workbenchStore.devServerStatus.get() === 'ready' &&
    s.devServerProcess &&
    s.devServerKind === 'dev'
  ) {
    return true;
  }
  await spawnViteServer(wc, 'dev', { forceRestart: true });
  return waitForDevServerReady(RECOVERY_SERVER_READY_TIMEOUT_MS);
}

async function restartDevServerAndWait(wc: WebContainer): Promise<boolean> {
  await spawnViteServer(wc, 'dev', { forceRestart: true, keepRestartBudget: true });
  return waitForDevServerReady(RECOVERY_SERVER_READY_TIMEOUT_MS);
}

/**
 * Writes every file and asset in `workbenchStore` into `wc`. Unlike `persistUserFile`, this
 * does not skip files whose store content is unchanged, because the container copy is the
 * one that was lost.
 */
async function writeWorkspaceToContainer(wc: WebContainer): Promise<void> {
  const files = Object.entries(workbenchStore.files.get());
  const dirs = new Set<string>();
  for (const [filePath] of files) {
    const dir = filePath.includes('/') ? filePath.substring(0, filePath.lastIndexOf('/')) : '';
    if (dir) dirs.add(dir);
  }
  for (const d of [...dirs].sort((a, b) => a.split('/').length - b.split('/').length)) {
    await wc.fs.mkdir(d, { recursive: true });
  }
  await runPool(files, TEMPLATE_FS_CONCURRENCY, ([filePath, file]) =>
    wc.fs.writeFile(filePath, file.content),
  );
  const assets = Object.entries(workbenchStore.assets.get());
  await runPool(assets, TEMPLATE_FS_CONCURRENCY, ([filePath, asset]) =>
    persistAssetFile(wc, filePath, asset.base64, asset.mimeType),
  );
}

async function remountDependencies(): Promise<boolean> {
  const wc = getPersisted().instance;
  if (!wc) return false;
  await killDevServer();

  const lockContent = getHeadlessTemplateFiles()['package-lock.json'];
  if (!lockContent) return false;
  const lockHash = getTemplateLockHash(lockContent);
  const mounted = await withDeadline(
    (async () => {
      const buffer =
        (await readDepsCacheFromOpfs(lockHash)) ??
        (await fetchPrebuiltNodeModules(lockHash, getPrebuiltNodeModulesUrl(lockHash)));
      if (!buffer) return false;
      try {
        await wc.fs.rm('node_modules', { recursive: true, force: true });
      } catch {
        /* mount below overwrites what is left */
      }
      return mountDepsFromBuffer(wc, buffer);
    })(),
    RECOVERY_REMOUNT_DEADLINE_MS,
    'Remounting node_modules',
  );
  if (!mounted) return false;
  appendTerminalLine('Remounted node_modules from the dependency cache.', 'info');

  await withDeadline(
    writeWorkspaceToContainer(wc),
    RECOVERY_WORKSPACE_WRITE_DEADLINE_MS,
    'Writing the workspace files',
  );
  return restartDevServerAndWait(wc);
}

async function rebootWebContainer(): Promise<boolean> {
  teardownWebContainer();
  workbenchStore.webcontainer.set(null);
  await sleep(REBOOT_SETTLE_MS);

  const wc = await bootWebContainer();
  await withDeadline(
    waitForTemplateReady(),
    RECOVERY_TEMPLATE_DEADLINE_MS,
    'Preparing the restarted workspace',
  );
  workbenchStore.webcontainer.set(wc);
  workbenchStore.bootStatus.set('ready');
  workbenchStore.bootError.set(undefined);

  await withDeadline(
    writeWorkspaceToContainer(wc),
    RECOVERY_WORKSPACE_WRITE_DEADLINE_MS,
    'Writing the workspace files',
  );
  return restartDevServerAndWait(wc);
}

function laterRecoveryStep(
  a: WorkbenchPreviewRecoveryStep,
  b: WorkbenchPreviewRecoveryStep | undefined,
): WorkbenchPreviewRecoveryStep {
  if (!b) return a;
  return PREVIEW_RECOVERY_STEPS.indexOf(b) > PREVIEW_RECOVERY_STEPS.indexOf(a) ? b : a;
}

/**
 * Brings a dead or silent preview back without a page refresh, trying the steps in
 * `runPreviewRecovery` (`./previewRecovery.ts`). Concurrent calls share one run. Progress is
 * published on `workbenchStore.previewRecovery`; resolves true when the dev server is ready again.
 */
export function recoverPreviewEnvironment(
  reason: WorkbenchPreviewRecoveryReason,
  opts?: { startAt?: WorkbenchPreviewRecoveryStep },
): Promise<boolean> {
  const s = getPersisted();
  if (s.recoveryPromise) return s.recoveryPromise;
  if (!s.instance) return Promise.resolve(false);

  const history = (s.recoveryHistory ??= []);
  const escalated = nextPreviewRecoveryStart(history, Date.now());
  if (escalated === null) {
    appendTerminalLine('The preview keeps failing in this tab; reload the page to start fresh.', 'stderr');
    console.warn('[workbench] preview recovery exhausted', { reason });
    workbenchStore.previewRecovery.set({ status: 'failed', reason });
    return Promise.resolve(false);
  }
  const planned = laterRecoveryStep(escalated, opts?.startAt);
  const instance = s.instance;

  appendTerminalLine(`Preview stopped responding (${reason}). Recovering…`, 'info');
  workbenchStore.previewRecovery.set({ status: 'recovering', reason, step: planned, attempt: 1 });

  // Set once the run settles or its deadline passes, so a step that returns afterwards cannot
  // flip the published state back to `recovering`.
  let settled = false;
  const run = (async (): Promise<boolean> => {
    const responsive = await isWebContainerResponsive(instance);
    const startAt = recoveryStartForWorkspace(planned, responsive);
    if (!responsive) {
      appendTerminalLine('The in-browser workspace is not responding; restarting it.', 'info');
    }

    let outcome: { recovered: boolean; step?: WorkbenchPreviewRecoveryStep };
    try {
      outcome = await withDeadline(
        runPreviewRecovery(
          {
            restartDevServer: async () => {
              const wc = s.instance;
              return wc ? restartDevServerAndWait(wc) : false;
            },
            remountDependencies,
            rebootWebContainer,
          },
          {
            startAt,
            onStep: (step, attempt) => {
              if (settled) return;
              console.info('[workbench] preview recovery step', { reason, step, attempt });
              appendTerminalLine(`Recovery: ${step} (attempt ${attempt})`, 'info');
              workbenchStore.previewRecovery.set({ status: 'recovering', reason, step, attempt });
            },
          },
        ),
        PREVIEW_RECOVERY_DEADLINE_MS,
        'Recovering the preview',
      );
    } catch (err) {
      appendTerminalLine(err instanceof Error ? err.message : String(err), 'stderr');
      outcome = { recovered: false };
    }
    settled = true;

    const { recovered, step } = outcome;
    history.push({ at: Date.now(), reason, recovered, step });
    if (history.length > 10) history.shift();
    console.info('[workbench] preview recovery finished', { reason, recovered, step });
    workbenchStore.previewRecovery.set(recovered ? { status: 'idle' } : { status: 'failed', reason });
    return recovered;
  })().finally(() => {
    if (s.recoveryPromise === run) s.recoveryPromise = null;
  });

  s.recoveryPromise = run;
  return run;
}

/** "Try again" after recovery failed: forget earlier runs and start from the first step. */
export function retryPreviewRecovery(): Promise<boolean> {
  const s = getPersisted();
  s.recoveryHistory = [];
  return recoverPreviewEnvironment('preview-silent');
}

/** Start `vite preview` when production build succeeded but no preview URL is available. */
export async function ensureProductionPreviewServer(wc: WebContainer): Promise<void> {
  await waitForTemplateReady();
  const url = workbenchStore.previewUrl.get();
  const status = workbenchStore.devServerStatus.get();
  if (url && status === 'ready') {
    return;
  }
  await spawnViteServer(wc, 'preview', { forceRestart: true });
}

export interface InteractiveShell {
  write: (data: string) => void;
  resize: (cols: number, rows: number) => void;
  kill: () => Promise<void>;
}

export async function startInteractiveShell(
  wc: WebContainer,
  opts: {
    cols: number;
    rows: number;
    onData: (chunk: string) => void;
    onExit?: (code: number) => void;
  },
): Promise<InteractiveShell> {
  await waitForTemplateReady();

  const process = await wc.spawn('jsh', {
    terminal: { cols: opts.cols, rows: opts.rows },
  });

  const inputWriter = process.input.getWriter();

  void process.output.pipeTo(
    new WritableStream<string>({
      write(chunk) {
        opts.onData(chunk);
      },
    }),
  );

  void process.exit.then((code) => opts.onExit?.(code));

  return {
    write: (data: string) => {
      void inputWriter.write(data);
    },
    resize: (cols: number, rows: number) => {
      (process as unknown as { resize?: (d: { cols: number; rows: number }) => void }).resize?.({
        cols,
        rows,
      });
    },
    kill: () => {
      const r = process.kill() as unknown;
      return r instanceof Promise ? r : Promise.resolve();
    },
  };
}
